// GlobalStyles for SearchPage.content's ModalInMobile wrapper/container classes.
// These land on Modal's in-React-tree Boxes via className passthrough, but Modal also
// applies emotion sx to the same Boxes. emotion <GlobalStyles> are inserted as base styles,
// so the component sx still wins on conflicts (matching the former css-module precedence).
// Page-prefixed class names avoid collision with BookmarkPage's same-named source classes.
import GlobalStyles from '@mui/material/GlobalStyles';
import React from 'react';

import { isCordova } from '../../util/mobile/deviceDetection';

export const SEARCH_PAGE_MAP_PANEL = 'searchPageMapPanel';
export const SEARCH_PAGE_MAP_PANEL_STORYBLOK = 'searchPageMapPanelStoryblok';
export const SEARCH_PAGE_MODAL_CONTAINER = 'searchPageModalContainer';
export const SEARCH_PAGE_MODAL_STORYBLOK_CONTAINER = 'searchPageModalStoryblokContainer';

/**
 * @param {import('@mui/material/styles').Theme} theme
 * @param {{ inApp?: boolean }} [context] - The app has no TopBar, so the map sticks at the top there.
 * @returns {object} GlobalStyles rules
 */
export const getSearchPageContentStyles = (theme, { inApp = false } = {}) => ({
  [`.${SEARCH_PAGE_MAP_PANEL}`]: {
    [theme.breakpoints.up('md')]: { display: 'flex' },
  },
  [`.${SEARCH_PAGE_MODAL_CONTAINER}`]: {
    ...theme.mixins.marketplaceModalInMobileBaseStyles,
    [theme.breakpoints.up('md')]: {
      ...theme.mixins.marketplaceModalInMobileBaseStyles['@media (min-width:900px)'],
    },
  },
  // Storyblok map column: its container fills the 37.5vw map panel (width 100% / flex-grow)
  // instead of being centered by the modal wrapper, so the map is not narrower than its column.
  [`.${SEARCH_PAGE_MODAL_STORYBLOK_CONTAINER}`]: {
    ...theme.mixins.marketplaceModalInMobileBaseStyles,
    [theme.breakpoints.up('md')]: {
      ...theme.mixins.marketplaceModalInMobileBaseStyles['@media (min-width:900px)'],
      position: 'sticky',
      top: inApp ? 0 : theme.tokens.topbarHeightDesktop,
      right: 0,
      flexGrow: 1,
      flexBasis: 'auto',
      width: '100%',
    },
  },
  // Storyblok-only: the map column is in-flow (sticky), so the ModalInMobile panel takes the
  // map's 37.5vw width (not Modal's default full width), and its scroll layer must not become a
  // scroll container (overflow:auto would trap sticky). Two-class / child selectors raise
  // specificity above Modal's own emotion sx.
  [`.${SEARCH_PAGE_MAP_PANEL}.${SEARCH_PAGE_MAP_PANEL_STORYBLOK}`]: {
    [theme.breakpoints.up('md')]: {
      flex: '0 0 37.5vw',
      maxWidth: '37.5vw',
    },
  },
  // The scroll layer (Modal's direct child). On desktop the Storyblok map panel renders inline,
  // so Modal's `isOpen` is false and the scroll-layer base styles (which carried `flexGrow:1`
  // via marketplaceModalRootStyles) are no longer applied since 4e29fe2ad (Safari grey-box fix).
  // Without them the scroll layer collapses to width 0 — its only content is the position:sticky
  // map column — so the map column (width:100%) and the Mapbox container also collapse to 0 and
  // the map never initializes. Restore the column-filling width here (flexGrow fills the flex-row
  // map panel) WITHOUT the backdrop dim / -webkit-fill-available that caused the grey box.
  [`.${SEARCH_PAGE_MAP_PANEL_STORYBLOK} > div`]: {
    [theme.breakpoints.up('md')]: {
      flexGrow: 1,
      width: '100%',
      overflow: 'visible',
      backgroundColor: 'transparent',
    },
  },
});

export const SearchPageContentGlobalStyles = () => {
  const inApp = isCordova();
  return <GlobalStyles styles={(theme) => getSearchPageContentStyles(theme, { inApp })} />;
};
